import type { RealtimeRun } from '@trigger.dev/sdk'
import { useRealtimeRun } from '@trigger.dev/react-hooks'

import type { CreateMembershipResponse, NewMembershipTrigger } from '@package/trigger/frontend'
import { triggerParseStatus } from '@package/trigger/frontend'

interface GenerateFunctioDocsStatus {
  triggerState: 'running' | 'completed'
  state: 'loading' | 'success' | 'error'
  progress: number
  label: string
  output?: CreateMembershipResponse
}

/**
 * Hook that subscribes to the generateFunctionDocs task and returns the status and output of the task.
 *
 * Uses the `useRealtimeRun` hook to subscribe to the task.
 *
 * See more about the `useRealtimeRun` hook in the [Trigger docs](https://trigger.dev/docs/frontend/react-hooks#userealtimerun).
 *
 * @param id the run id of the generateFunctionDocs task
 */
export function useTriggerStatusMembership(
  id: string,
  options?: { accessToken?: string },
): {
  status: GenerateFunctioDocsStatus
  error: Error | undefined
  run: RealtimeRun<NewMembershipTrigger> | undefined
} {
  const { run, error } = useRealtimeRun<NewMembershipTrigger>(id, { accessToken: options?.accessToken })

  const status: GenerateFunctioDocsStatus = {
    triggerState: run?.status === 'COMPLETED' ? 'completed' : 'running',
    state: 'loading',
    progress: 0,
    label: 'Sedang memverifikasi pembayaran...',
    output: run?.output ?? undefined,
  }

  // Parse metadata if available
  if (run?.metadata) {
    const { progress, label, state } = triggerParseStatus(run.metadata)
    status.progress = progress
    status.label = label
    status.state = state
  }

  return {
    status,
    error,
    run,
  }
}
